/**
 * useCreateTicketMessage Hook
 *
 * React Query mutation for adding a message to a support ticket.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { supportTicketService } from '../../../api/support-ticket.service';
import type {
  TicketMessageSanitized,
  TicketMessageCreateDTO,
} from '../../../types/support-ticket.types';

interface UseCreateTicketMessageOptions {
  tenantId: string;
  ticketId: string;
  onSuccess?: (data: TicketMessageSanitized) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for adding a message to a ticket
 */
export const useCreateTicketMessage = (
  options: UseCreateTicketMessageOptions
): UseMutationResult<TicketMessageSanitized, Error, TicketMessageCreateDTO> => {
  const { tenantId, ticketId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<TicketMessageSanitized, Error, TicketMessageCreateDTO>({
    mutationFn: async (data: TicketMessageCreateDTO) => {
      return await supportTicketService.createMessage(tenantId, ticketId, data);
    },
    onSuccess: (data) => {
      // Invalidate ticket messages
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', tenantId, ticketId] });
      // Invalidate ticket to update message count
      queryClient.invalidateQueries({ queryKey: ['support-ticket', tenantId, ticketId] });
      // Invalidate tickets list to update lastMessageAt
      queryClient.invalidateQueries({ queryKey: ['support-tickets', tenantId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
